import { NAV } from "./routes";

export type SearchKind = "view" | "market" | "wallet";

export interface SearchItem {
	kind: SearchKind;
	/** Path, condition id or address, depending on kind. */
	id: string;
	title: string;
	sub?: string;
	/** Lower-cased text to match against, built once. */
	text: string;
	/** Breaks ties: higher comes first. */
	weight: number;
}

export const ADDRESS_RE = /^0x[0-9a-f]{40}$/i;
const CONDITION_RE = /^0x[0-9a-f]{64}$/i;

export const item = (kind: SearchKind, id: string, title: string, sub = "", extra = "", weight = 0): SearchItem => ({
	kind,
	id,
	title,
	sub,
	text: `${title} ${extra} ${id}`.toLowerCase(),
	weight,
});

/** Every place the desk can go, from the nav plus the lists inside Unusual Markets. */
export function viewItems(): SearchItem[] {
	const leaves = NAV.flatMap((n) => {
		const kids = (n.children ?? []).map((c) => item("view", c.href, c.label, n.label, n.label));
		return kids.length ? kids : [item("view", n.href, n.label, "", n.href === "/" ? "home unusual" : "")];
	});
	return [
		...leaves,
		item("view", "/?tab=scheduled", "Scheduled markets", "Markets", "fixtures games ending soon"),
		item("view", "/?tab=new", "New markets", "Markets", "recent"),
		item("view", "/?tab=past_end", "Past end date", "Markets", "awaiting result resolution"),
	];
}

/** Later lists lose duplicates to earlier ones: the first has the richer row. */
export function mergeItems(...lists: SearchItem[][]): SearchItem[] {
	const seen = new Set<string>();
	const out: SearchItem[] = [];
	for (const list of lists) {
		for (const i of list) {
			const k = `${i.kind}:${i.id.toLowerCase()}`;
			if (seen.has(k)) continue;
			seen.add(k);
			out.push(i);
		}
	}
	return out;
}

function matchRank(title: string, needle: string): number {
	if (title.startsWith(needle)) return 3;
	if (title.includes(` ${needle}`)) return 2;
	return title.includes(needle) ? 1 : 0;
}

export interface SearchGroups {
	view: SearchItem[];
	market: SearchItem[];
	wallet: SearchItem[];
}

const PER_GROUP = { view: 4, market: 7, wallet: 5 } as const;

/**
 * Items holding every word of `query`, best first, capped per group (pure). A pasted address
 * or condition id always yields a row of its own, whether or not it is loaded.
 */
export function searchItems(items: SearchItem[], query: string): SearchGroups {
	const needle = query.trim().toLowerCase();
	const words = needle.split(/\s+/).filter(Boolean);
	const hits: { i: SearchItem; rank: number }[] = [];
	for (const i of items) {
		if (words.every((w) => i.text.includes(w))) {
			hits.push({ i, rank: needle ? matchRank(i.title.toLowerCase(), needle) : 0 });
		}
	}
	hits.sort((a, b) => b.rank - a.rank || b.i.weight - a.i.weight);
	const group = (kind: SearchKind) => hits.filter((h) => h.i.kind === kind).slice(0, PER_GROUP[kind]).map((h) => h.i);
	const out: SearchGroups = { view: group("view"), market: group("market"), wallet: group("wallet") };
	const raw = query.trim();
	if (ADDRESS_RE.test(raw) && !out.wallet.some((w) => w.id.toLowerCase() === needle)) {
		out.wallet.unshift(item("wallet", raw, raw, "wallet address"));
	}
	if (CONDITION_RE.test(raw) && !out.market.some((m) => m.id.toLowerCase() === needle)) {
		out.market.unshift(item("market", raw, raw, "condition id"));
	}
	return out;
}
